import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CATEGORY_ICONS } from "../lib/constants";
import { useT, useI18n } from "../i18n/I18nContext";
import { Sparkles, MapPin } from "lucide-react";

function useFmtDate() {
  const { lang } = useI18n();
  const locale = { it: "it-IT", en: "en-GB", de: "de-DE", es: "es-ES", fr: "fr-FR", ja: "ja-JP" }[lang] || "it-IT";
  return (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
    } catch { return iso; }
  };
}

export default function Wall() {
  const t = useT();
  const fmtDate = useFmtDate();
  const [data, setData] = useState({ items: [], stats: { today: 0, week: 0, total: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/wall")
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16 anim-fade-up" data-testid="wall-page">
      <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">
        {t("wall.overline")}
      </div>
      <h1 className="text-3xl md:text-5xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
        {t("wall.title")}
      </h1>
      <p className="text-mabw-soft mt-3 max-w-2xl">{t("wall.sub")}</p>

      <div className="mt-10 grid grid-cols-3 gap-4 max-w-2xl" data-testid="wall-stats">
        <Stat label={t("wall.stat_today")} value={data.stats.today} />
        <Stat label={t("wall.stat_week")} value={data.stats.week} />
        <Stat label={t("wall.stat_total")} value={data.stats.total} />
      </div>

      <div className="mt-12 space-y-4">
        {loading && <div className="text-mabw-soft" data-testid="wall-loading">{t("common.loading")}</div>}
        {!loading && data.items.length === 0 && (
          <div className="card-mabw p-10 text-center text-mabw-soft" data-testid="wall-empty">
            {t("wall.empty")}
          </div>
        )}
        {data.items.map((it, i) => {
          const Icon = CATEGORY_ICONS[it.category] || CATEGORY_ICONS.other;
          return (
            <div
              key={it.id}
              className="card-mabw p-5 md:p-6 flex items-start gap-4"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`wall-item-${it.id}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-mabw-secondary/15 text-mabw-secondary flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm md:text-base text-mabw-text leading-relaxed">
                  <Sparkles className="w-4 h-4 inline-block text-mabw-accent mr-1.5 -mt-0.5" />
                  {t("wall.anonymous_help")}
                  {it.city && <> · <MapPin className="w-4 h-4 inline-block text-mabw-soft mr-0.5 -mt-0.5" /> {it.city}</>}
                </div>
                <div className="text-xs text-mabw-soft mt-1">
                  {fmtDate(it.date)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-3xl md:text-4xl font-semibold text-mabw-primary" style={{ fontFamily: "Outfit" }}>{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-mabw-soft mt-1">{label}</div>
    </div>
  );
}
